import { WaitlistEntry, WaitlistEntryInput } from '../../model/waitlist/WaitlistEntry';
import i18n from "../../i18n";

const API_URL = process.env.REACT_APP_CALENDAR_SERVICE_BASE_URL || '';

const FIELDS = `
    id
    businessId
    clientId
    customerName
    customerPhone
    customerEmail
    teamMemberId
    serviceIds
    validUntil
    priority
    desiredStartTime
    desiredEndTime
    preferredTimes
    notes
    status
    convertedAppointmentId
    createdBy
    createdOn
    updatedOn
`;

async function gqlPost<T>(token: string, query: string, variables?: object): Promise<T> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors[0]?.message ?? 'GraphQL error');
    return json.data as T;
}

export async function getWaitlistEntriesByBusiness(
    businessId: string,
    token: string
): Promise<WaitlistEntry[]> {
    const data = await gqlPost<{ getWaitlistEntriesByBusiness: WaitlistEntry[] | null }>(
        token,
        `query getWaitlistEntriesByBusiness($businessId: ID!) {
            getWaitlistEntriesByBusiness(businessId: $businessId) { ${FIELDS} }
        }`,
        { businessId }
    );
    return data.getWaitlistEntriesByBusiness ?? [];
}

export async function saveWaitlistEntry(
    input: WaitlistEntryInput,
    token: string
): Promise<WaitlistEntry> {
    const data = await gqlPost<{ saveWaitlistEntry: WaitlistEntry }>(
        token,
        `mutation saveWaitlistEntry($input: WaitlistEntryInput!) {
            saveWaitlistEntry(input: $input) { ${FIELDS} }
        }`,
        { input }
    );
    return data.saveWaitlistEntry;
}

export async function cancelWaitlistEntry(id: string, token: string): Promise<boolean> {
    const data = await gqlPost<{ cancelWaitlistEntry: boolean }>(
        token,
        `mutation cancelWaitlistEntry($id: ID!) { cancelWaitlistEntry(id: $id) }`,
        { id }
    );
    return data.cancelWaitlistEntry;
}

/**
 * Convert a waitlist entry into a real appointment. Backend validates that
 * the team member is actually free in the requested window; on success the
 * entry's status becomes CONVERTED. Returns the new appointment's id.
 */
export async function scheduleWaitlistEntry(
    id: string,
    startTime: string,
    endTime: string,
    token: string
): Promise<{ id: string; startTime: string; endTime: string }> {
    const data = await gqlPost<{ scheduleWaitlistEntry: { id: string; startTime: string; endTime: string } }>(
        token,
        `mutation scheduleWaitlistEntry($id: ID!, $startTime: String!, $endTime: String!) {
            scheduleWaitlistEntry(id: $id, startTime: $startTime, endTime: $endTime) {
                id startTime endTime
            }
        }`,
        { id, startTime, endTime }
    );
    return data.scheduleWaitlistEntry;
}

export async function deleteWaitlistEntry(id: string, token: string): Promise<boolean> {
    const data = await gqlPost<{ deleteWaitlistEntry: boolean }>(
        token,
        `mutation deleteWaitlistEntry($id: ID!) { deleteWaitlistEntry(id: $id) }`,
        { id }
    );
    return data.deleteWaitlistEntry;
}
